
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  HStack,
  Text,
  SimpleGrid,
  useColorModeValue,
  Icon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  VStack,
  Flex,
  ButtonGroup,
  Portal,
} from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';

interface MonthRangePickerProps {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  onRangeSelect: (startDate: string, endDate: string) => void;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

type PickerMode = 'MANUAL' | 'MONTH' | 'YEAR';

export const MonthYearPicker: React.FC<MonthRangePickerProps> = ({ startDate, endDate, onRangeSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<PickerMode>('MANUAL');

  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 3;

  const [manualStartYear, setManualStartYear] = useState<number>(currentYear);
  const [manualStartMonth, setManualStartMonth] = useState<number>(new Date().getMonth());
  const [manualEndYear, setManualEndYear] = useState<number>(currentYear);
  const [manualEndMonth, setManualEndMonth] = useState<number>(new Date().getMonth());
  const [focusYear, setFocusYear] = useState<number>(currentYear);

  // All color tokens at top level — never inside conditionals
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const manualActiveBg = useColorModeValue('blue.50', 'blue.900');
  const monthActiveBg = useColorModeValue('blue.50', 'blue.900');
  const yearActiveBg = useColorModeValue('blue.50', 'blue.900');

  const btnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePos();
      window.addEventListener('scroll', updatePos, true);
      window.addEventListener('resize', updatePos);
      return () => {
        window.removeEventListener('scroll', updatePos, true);
        window.removeEventListener('resize', updatePos);
      };
    }
  }, [isOpen, updatePos]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (startDate) {
      const d = new Date(startDate);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        setManualStartYear(Math.max(minYear, Math.min(currentYear, year)));
        setManualStartMonth(d.getMonth());
        setFocusYear(Math.max(minYear, Math.min(currentYear, year)));
      }
    }
    if (endDate) {
      const d = new Date(endDate);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        setManualEndYear(Math.max(minYear, Math.min(currentYear, year)));
        setManualEndMonth(d.getMonth());
      }
    }
  }, [startDate, endDate, minYear, currentYear]);

  const formatDate = (y: number, m: number, d: number) => {
    const month = String(m + 1).padStart(2, '0');
    const day = String(d).padStart(2, '0');
    return `${y}-${month}-${day}`;
  };

  const selectYear = (year: number) => {
    onRangeSelect(formatDate(year, 0, 1), formatDate(year, 11, 31));
    setIsOpen(false);
  };

  const selectMonth = (year: number, monthIndex: number) => {
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    onRangeSelect(formatDate(year, monthIndex, 1), formatDate(year, monthIndex, lastDay));
    setIsOpen(false);
  };

  const applyManualRange = (sYear: number, sMonth: number, eYear: number, eMonth: number) => {
    const endObjLast = new Date(eYear, eMonth + 1, 0);
    onRangeSelect(formatDate(sYear, sMonth, 1), formatDate(eYear, eMonth, endObjLast.getDate()));
  };

  const handleManualChange = (type: 'start' | 'end', field: 'year' | 'month', val: number) => {
    let newSY = manualStartYear;
    let newSM = manualStartMonth;
    let newEY = manualEndYear;
    let newEM = manualEndMonth;

    if (type === 'start') {
      if (field === 'year') newSY = val;
      if (field === 'month') newSM = val;
      setManualStartYear(newSY);
      setManualStartMonth(newSM);
    } else {
      if (field === 'year') newEY = val;
      if (field === 'month') newEM = val;
      setManualEndYear(newEY);
      setManualEndMonth(newEM);
    }

    applyManualRange(newSY, newSM, newEY, newEM);
  };

  const renderYearMode = () => {
    const years = Array.from({ length: currentYear - minYear + 1 }, (_, i) => currentYear - i);
    return (
      <Box pt={2} maxHeight="200px" overflowY="auto">
        <VStack align="stretch" spacing={1}>
          {years.map(y => (
            <Button
              key={y}
              variant={y === focusYear ? 'solid' : 'ghost'}
              onClick={() => selectYear(y)}
              colorScheme={y === focusYear ? 'blue' : 'gray'}
              justifyContent="center"
              size="sm"
            >
              {y}
            </Button>
          ))}
        </VStack>
      </Box>
    );
  };

  const renderMonthMode = () => (
    <VStack pt={2} align="stretch" spacing={4}>
      <HStack justify="center">
        <Button size="sm" onClick={() => setFocusYear(prev => Math.max(minYear, prev - 1))}>&lt;</Button>
        <Text fontWeight="bold">{focusYear}</Text>
        <Button size="sm" onClick={() => setFocusYear(prev => Math.min(currentYear, prev + 1))}>&gt;</Button>
      </HStack>
      <SimpleGrid columns={3} spacing={3}>
        {MONTHS.map((m, idx) => (
          <Button key={m} variant="ghost" onClick={() => selectMonth(focusYear, idx)}>
            {m}
          </Button>
        ))}
      </SimpleGrid>
    </VStack>
  );

  const renderManualMode = () => (
    <Flex direction={{ base: "column", md: "row" }} gap={6} pt={2}>
      <VStack align="stretch" flex={1}>
        <Text fontWeight="bold" fontSize="xs" color="gray.500" textTransform="uppercase">From</Text>
        <NumberInput size="sm" value={manualStartYear}
          onChange={(_, val) => handleManualChange('start', 'year', val)}
          min={minYear} max={currentYear}>
          <NumberInputField />
          <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
        </NumberInput>
        <SimpleGrid columns={3} spacing={2}>
          {MONTHS.map((m, idx) => (
            <Button key={`start-${m}`} size="xs"
              variant={manualStartMonth === idx ? "solid" : "ghost"}
              colorScheme={manualStartMonth === idx ? "blue" : "gray"}
              onClick={() => handleManualChange('start', 'month', idx)}>
              {m}
            </Button>
          ))}
        </SimpleGrid>
      </VStack>

      <Box width="1px" bg="gray.200" display={{ base: "none", md: "block" }} />
      <Divider display={{ base: "block", md: "none" }} />

      <VStack align="stretch" flex={1}>
        <Text fontWeight="bold" fontSize="xs" color="gray.500" textTransform="uppercase">To</Text>
        <NumberInput size="sm" value={manualEndYear}
          onChange={(_, val) => handleManualChange('end', 'year', val)}
          min={minYear} max={currentYear}>
          <NumberInputField />
          <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
        </NumberInput>
        <SimpleGrid columns={3} spacing={2}>
          {MONTHS.map((m, idx) => (
            <Button key={`end-${m}`} size="xs"
              variant={manualEndMonth === idx ? "solid" : "ghost"}
              colorScheme={manualEndMonth === idx ? "blue" : "gray"}
              onClick={() => handleManualChange('end', 'month', idx)}>
              {m}
            </Button>
          ))}
        </SimpleGrid>
      </VStack>
    </Flex>
  );

  const getLabel = () => {
    if (!startDate || !endDate) return "Select Range";
    return `${startDate} → ${endDate}`;
  };

  return (
    <Box position="relative" display="inline-block">
      <Button
        ref={btnRef}
        leftIcon={<CalendarIcon />}
        variant="outline"
        bg={bg}
        borderColor={borderColor}
        width="300px"
        justifyContent="flex-start"
        onClick={() => setIsOpen(v => !v)}
        fontSize="sm"
      >
        {getLabel()}
      </Button>

      {isOpen && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="fixed"
            top={`${dropdownPos.top}px`}
            left={`${dropdownPos.left}px`}
            zIndex={2000}
            p={4}
            bg={bg}
            borderColor={borderColor}
            borderWidth="1px"
            borderRadius="md"
            boxShadow="xl"
            width={{ base: "320px", md: "520px" }}
          >
            <VStack align="stretch" spacing={4}>
              <ButtonGroup size="sm" isAttached variant="outline" width="full">
                <Button
                  flex={1}
                  colorScheme={mode === 'MANUAL' ? 'blue' : 'gray'}
                  bg={mode === 'MANUAL' ? manualActiveBg : undefined}
                  onClick={() => setMode('MANUAL')}
                >
                  Manual
                </Button>
                <Button
                  flex={1}
                  colorScheme={mode === 'MONTH' ? 'blue' : 'gray'}
                  bg={mode === 'MONTH' ? monthActiveBg : undefined}
                  onClick={() => setMode('MONTH')}
                >
                  Month
                </Button>
                <Button
                  flex={1}
                  colorScheme={mode === 'YEAR' ? 'blue' : 'gray'}
                  bg={mode === 'YEAR' ? yearActiveBg : undefined}
                  onClick={() => setMode('YEAR')}
                >
                  Year
                </Button>
              </ButtonGroup>

              <Divider />

              {mode === 'MANUAL' && renderManualMode()}
              {mode === 'MONTH' && renderMonthMode()}
              {mode === 'YEAR' && renderYearMode()}
            </VStack>
          </Box>
        </Portal>
      )}
    </Box>
  );
};
